/**
 * Live lookups for the Infrastructure flow: regions, resource groups, VNets with their subnets and free addresses, the existing servers
 * (the Domain level's existing environment, the AppCentral level's RDS and Azure SQL servers, an existing SQL server's image and
 * network) and the next free server name number, read from Azure by the backend (`/api/v1/infrastructure/...`). Only the environments
 * listed in `LIVE_INFRA_ENVIRONMENTS` use them, and for those the mock never answers an existing-server question: it would invent
 * servers that do not exist. Every other lookup, and every other environment, is still answered by the mock.
 */
import type { AxiosResponse } from 'axios'
import { api } from '@/services/api'
import type {
  ExistingEnvironment,
  ExistingServers,
  IisServer,
  InfraProduct,
  InfraRegion,
  InfraResourceGroup,
  InfraVnet,
  PublicIpQuota,
  PublicIpQuotaQuery,
  SqlServerNetworkAndImage,
  VmSizes,
  VmSizesQuery,
} from '@/types/infrastructure'
import type { ServerRole, ServerSequence } from '@/utils/naming'
import type { InfraService } from '@/services/infraService'

/**
 * Environments read live. The backend decides which Azure subscription and tenant each one reads: staging is Traverse-RnD in
 * Aptean-online; demo (a-dev-001) and production are in aptean-cloud. A build can read fewer of them live with
 * `VITE_INFRA_LIVE_ENVIRONMENTS` (comma separated); the tests set it to `stg`, so the mock still answers demo and production there.
 */
export const DEFAULT_LIVE_INFRA_ENVIRONMENTS: readonly string[] = ['stg', 'demo', 'production']

export function liveEnvironments(configured: string | undefined): readonly string[] {
  const listed = (configured ?? '').split(',').map((value) => value.trim()).filter(Boolean)
  return listed.length > 0 ? listed : DEFAULT_LIVE_INFRA_ENVIRONMENTS
}

export const LIVE_INFRA_ENVIRONMENTS: readonly string[] = liveEnvironments(import.meta.env.VITE_INFRA_LIVE_ENVIRONMENTS as string | undefined)

/** A live lookup that failed, with a message the user can act on (the backend's own, never Azure's raw error). */
export class LiveLookupError extends Error {
  readonly status: number | null

  constructor(message: string, status: number | null = null) {
    super(message)
    this.name = 'LiveLookupError'
    this.status = status
  }
}

/** The message to show for a failed lookup, or undefined when it has none of its own (the select then says it in general terms). */
export function lookupErrorMessage(error: unknown): string | undefined {
  return error instanceof LiveLookupError ? error.message : undefined
}

// Resolve (don't reject) on everything but 401, so the client's global handling of other statuses never fires for a lookup.
const REQUEST_CONFIG = { validateStatus: (status: number) => status !== 401 } as const

function detailOf(response: AxiosResponse): string {
  const detail = (response.data as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  return `The lookup failed (HTTP ${response.status}).`
}

async function fetchLive<T>(path: string, params: Record<string, string>): Promise<T> {
  let response: AxiosResponse<T>
  try {
    response = await api.get<T>(path, { params, ...REQUEST_CONFIG })
  } catch (err) {
    if (err instanceof Error && 'response' in err && (err as { response?: { status?: number } }).response?.status === 401) {
      throw new LiveLookupError('Your session has expired. Sign in again.', 401)
    }
    throw new LiveLookupError('The portal could not be reached. Try again in a moment.')
  }
  if (response.status >= 400) throw new LiveLookupError(detailOf(response), response.status)
  return response.data
}

export type LiveLookups = Pick<
  InfraService,
  | 'getRegions'
  | 'getResourceGroups'
  | 'getVnets'
  | 'getExistingEnvironment'
  | 'getExistingSqlServerNetworkAndImage'
  | 'getExistingServers'
  | 'getExistingIisServers'
  | 'getNextServerSequence'
  | 'getVmSizes'
  | 'getPublicIpQuota'
>

const productParams = (product: InfraProduct) => ({ product: product.slug, ...(product.line ? { line: product.line } : {}) })

export function createLiveLookups(): LiveLookups {
  return {
    // Every region in which the environment's subscription has a resource group.
    getRegions: (environmentId: string, product: InfraProduct): Promise<InfraRegion[]> =>
      fetchLive('/infrastructure/regions/', { environment: environmentId, product: product.slug }),
    getResourceGroups: (environmentId: string, regionId: string, product: InfraProduct): Promise<InfraResourceGroup[]> =>
      fetchLive('/infrastructure/resource-groups/', { environment: environmentId, region: regionId, product: product.slug }),
    getVnets: (environmentId: string, regionId: string, resourceGroupId: string, product: InfraProduct): Promise<InfraVnet[]> =>
      fetchLive('/infrastructure/vnets/', {
        environment: environmentId,
        region: regionId,
        resource_group: resourceGroupId,
        product: product.slug,
      }),
    getExistingEnvironment: (product: InfraProduct, environmentId: string, regionId: string, resourceGroupId: string): Promise<ExistingEnvironment> =>
      fetchLive('/infrastructure/existing-environment/', { ...productParams(product), environment: environmentId, region: regionId, resource_group: resourceGroupId }),
    getExistingSqlServerNetworkAndImage: (product: InfraProduct, serverName: string, environmentId?: string): Promise<SqlServerNetworkAndImage> =>
      fetchLive(`/infrastructure/sql-servers/${encodeURIComponent(serverName)}/network-and-image/`, {
        ...productParams(product),
        ...(environmentId ? { environment: environmentId } : {}),
      }),
    getExistingServers: (product: InfraProduct, environmentId: string, regionId: string, resourceGroupId: string): Promise<ExistingServers> =>
      fetchLive('/infrastructure/existing-servers/', { ...productParams(product), environment: environmentId, region: regionId, resource_group: resourceGroupId }),
    getExistingIisServers: (environmentId: string, regionId: string): Promise<IisServer[]> =>
      fetchLive('/infrastructure/iis-servers/', { environment: environmentId, region: regionId }),
    getNextServerSequence: async (environmentId: string, product: InfraProduct, role: ServerRole): Promise<ServerSequence> => {
      const answer = await fetchLive<{ ok: boolean; problem?: string; next?: number; highestExisting?: string | null }>('/infrastructure/server-names/next/', {
        ...productParams(product),
        environment: environmentId,
        role,
      })
      // ok is false (with the reason) when no name can be generated, for example no prefix for the environment: said, not guessed at.
      if (!answer.ok || typeof answer.next !== 'number') throw new LiveLookupError(answer.problem ?? 'No server name can be generated for this server.')
      return { next: answer.next, highestExisting: answer.highestExisting ?? null }
    },
    getVmSizes: (query: VmSizesQuery): Promise<VmSizes> =>
      fetchLive('/infrastructure/vm-sizes/', {
        environment: query.environmentId,
        region: query.regionId,
        pool_tier: query.poolTier,
        rds: String(query.rds),
        db: String(query.db),
        hosts: String(query.hosts),
      }),
    getPublicIpQuota: (query: PublicIpQuotaQuery): Promise<PublicIpQuota> =>
      fetchLive('/infrastructure/public-ip-quota/', { environment: query.environmentId, region: query.regionId, count: String(query.count) }),
  }
}

/** `base` with the live lookups swapped in for the listed environments. */
export function withLiveLookups(base: InfraService, live: LiveLookups, environments: readonly string[]): InfraService {
  const isLive = (environmentId: string) => environments.includes(environmentId)
  return {
    ...base,
    getRegions: (environmentId, product) => (isLive(environmentId) ? live.getRegions(environmentId, product) : base.getRegions(environmentId, product)),
    getResourceGroups: (environmentId, regionId, product) =>
      isLive(environmentId) ? live.getResourceGroups(environmentId, regionId, product) : base.getResourceGroups(environmentId, regionId, product),
    getVnets: (environmentId, regionId, resourceGroupId, product) =>
      isLive(environmentId)
        ? live.getVnets(environmentId, regionId, resourceGroupId, product)
        : base.getVnets(environmentId, regionId, resourceGroupId, product),
    getExistingEnvironment: (product, environmentId, regionId, resourceGroupId) =>
      isLive(environmentId)
        ? live.getExistingEnvironment(product, environmentId, regionId, resourceGroupId)
        : base.getExistingEnvironment(product, environmentId, regionId, resourceGroupId),
    getExistingSqlServerNetworkAndImage: (product, serverName, environmentId) =>
      environmentId && isLive(environmentId)
        ? live.getExistingSqlServerNetworkAndImage(product, serverName, environmentId)
        : base.getExistingSqlServerNetworkAndImage(product, serverName, environmentId),
    getExistingServers: (product, environmentId, regionId, resourceGroupId) =>
      isLive(environmentId)
        ? live.getExistingServers(product, environmentId, regionId, resourceGroupId)
        : base.getExistingServers(product, environmentId, regionId, resourceGroupId),
    getNextServerSequence: (environmentId, product, role) =>
      isLive(environmentId) ? live.getNextServerSequence(environmentId, product, role) : base.getNextServerSequence(environmentId, product, role),
    getExistingIisServers: (environmentId, regionId) =>
      isLive(environmentId) ? live.getExistingIisServers(environmentId, regionId) : base.getExistingIisServers(environmentId, regionId),
    getVmSizes: (query) => (isLive(query.environmentId) ? live.getVmSizes(query) : base.getVmSizes(query)),
    getPublicIpQuota: (query) => (isLive(query.environmentId) ? live.getPublicIpQuota(query) : base.getPublicIpQuota(query)),
  }
}
